// Figma "Карта заявок": what the chosen window adds up to.
import { ConnectProto } from "@racoon/api";
import { Namespace, useLang, useT, type TranslationKey } from "@racoon/i18n";
import { formatVolume } from "~/shared/lib/job-metrics";

type Word = "orders" | "locations" | "containers";

/** The key of `word` for `count` under the language's plural rules. */
const pluralKey = (rules: Intl.PluralRules, word: Word, count: number) => {
  const form = rules.select(count);
  const key = form === "one" || form === "few" || form === "many" ? form : "other";
  return `jobMap.totals.${word}_${key}` as TranslationKey<Namespace.Orders>;
};

/**
 * Orders, pickup locations, planned containers and m³ across the whole window — every
 * order the filters match, not the pages loaded — as the server's "totals" facet adds
 * them up, keyed by JobTotal.
 */
export const JobMapTotals = ({ totals }: { totals?: ConnectProto.FacetCounts["counts"] }) => {
  const t = useT();
  const { lang } = useLang();
  if (!totals) return null;
  const rules = new Intl.PluralRules(lang);
  const total = (key: ConnectProto.JobTotal) => totals[key] ?? 0;
  const counted = (word: Word, count: number) =>
    t(Namespace.Orders, pluralKey(rules, word, count), { count });
  const items = [
    counted("orders", total(ConnectProto.JobTotal.ORDERS)),
    counted("locations", total(ConnectProto.JobTotal.PICKUP_LOCATIONS)),
    counted("containers", total(ConnectProto.JobTotal.CONTAINERS)),
    // Facet counts are whole numbers: the volume comes in litres.
    t(Namespace.Orders, "jobMap.volume", {
      volume: formatVolume(total(ConnectProto.JobTotal.VOLUME_LITRES) / 1000),
    }),
  ];
  return (
    <div className="flex h-8 items-center divide-x divide-slate-200 rounded-lg border border-slate-200 bg-white text-xs font-semibold uppercase text-text-subtle">
      {items.map((item) => (
        <span key={item} className="whitespace-nowrap px-4">
          {item}
        </span>
      ))}
    </div>
  );
};
